import React, { useEffect, useState } from 'react';
import { Card, Button, Badge, EmptyState, ErrorNote } from '../ui';

interface SegmentRow {
  id: string;
  partNumber: number;
  status: string;
  durationSec: number | null;
}

interface ScenarioRow {
  id: string;
  name: string;
  location: string | null;
  conflict: string | null;
  emotionalTone: string | null;
  coldOpen: string | null;
  finalHook: string | null;
  isWinner: boolean;
}

interface ReelRow {
  id: string;
  pageId: string;
  title: string | null;
  state: string;
  qualityScore: number | null;
  updatedAt: string;
  page: { internalLabel: string };
  reel: {
    titleTranslation: string | null;
    hookNumber: number | null;
    painTrigger: string | null;
    commentBaitAngle: string | null;
    caption: string | null;
    hashtags: string[];
    targetDurationSec: number;
    segmentDurationSec: number;
    combinedAssetId: string | null;
    thumbnailAssetId: string | null;
    overlayPlan: unknown;
    segments: SegmentRow[];
    scenarios: ScenarioRow[];
  } | null;
  qualityScores: { overall: number; explanation: string | null; blockingIssues: string[] }[];
  schedules: { publishAt: string; method: string; status: string }[];
  publications: { fbObjectId: string }[];
}

interface PageOption { id: string; internalLabel: string }

const STATE_BADGE: Record<string, string> = {
  PUBLISHED: 'HEALTHY', SCHEDULED: 'HEALTHY', APPROVED: 'HEALTHY',
  REVIEW_REQUIRED: 'RATE_LIMITED', RETRY_WAIT: 'RATE_LIMITED',
  BLOCKED: 'MISCONFIGURED', FAILED: 'MISCONFIGURED', CANCELLED: 'ARCHIVED',
};

const SEG_BADGE: Record<string, string> = {
  READY: 'HEALTHY', GENERATING: 'BUSY', PENDING: 'NOT_INSTALLED', FAILED: 'MISCONFIGURED',
};

export function ReelsScreen({ pageId }: { pageId?: string } = {}) {
  const [items, setItems] = useState<ReelRow[]>([]);
  const [pages, setPages] = useState<PageOption[]>([]);
  const [localFilter, setLocalFilter] = useState('');
  const pageFilter = pageId ?? localFilter;
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [media, setMedia] = useState<Record<string, string>>({}); // key: `${id}:${kind}`

  const load = async () => {
    try {
      setPages((await window.pagepilot.pages.list()) as PageOption[]);
      const rows = (await window.pagepilot.content.list(pageFilter || undefined, 'REEL')) as ReelRow[];
      setItems(rows);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };
  useEffect(() => {
    void load();
    const t = setInterval(() => void load(), 12_000);
    return () => clearInterval(t);
  }, [pageFilter]);

  const loadMedia = async (id: string, kind: string) => {
    const key = `${id}:${kind}`;
    if (media[key]) return;
    const url = (await window.pagepilot.content.assetDataUrl(id, kind)) as string | null;
    if (url) setMedia((prev) => ({ ...prev, [key]: url }));
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <h1 className="text-lg font-semibold">Reels</h1>
        {!pageId && (
          <select
            value={localFilter}
            onChange={(e) => setLocalFilter(e.target.value)}
            className="rounded-lg border border-neutral-700 bg-neutral-900 px-2 py-1.5 text-sm"
          >
            <option value="">All Pages</option>
            {pages.map((p) => (
              <option key={p.id} value={p.id}>{p.internalLabel}</option>
            ))}
          </select>
        )}
        <div className="ml-auto">
          <Button
            disabled={!pageFilter}
            title={pageFilter ? '' : 'Select a Page first'}
            onClick={async () => {
              await window.pagepilot.content.generateNow(pageFilter, 'REEL');
              void load();
            }}
          >
            Generate now
          </Button>
        </div>
      </div>
      {error && <ErrorNote message={error} />}
      {items.length === 0 && (
        <EmptyState
          title="No Reels yet"
          hint='Set "Reels per day" above 0 in the Page configuration, then Generate now. The pipeline creates the concept, conversation plan, reference image, video segments, joined video, thumbnail and caption. Video generation stays blocked until a supported Grok Imagine integration is configured.'
        />
      )}

      <div className="space-y-4">
        {items.map((item) => {
          const reel = item.reel;
          const winner = reel?.scenarios.find((s) => s.isWinner);
          return (
            <Card key={item.id}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold">{item.title ?? 'Generating concept…'}</p>
                  <p className="text-xs text-neutral-500">
                    {item.page.internalLabel} · {reel ? `${reel.targetDurationSec}s in ${reel.segments.length || Math.ceil(reel.targetDurationSec / reel.segmentDurationSec)}×${reel.segmentDurationSec}s segments` : ''}
                    {item.qualityScore !== null ? ` · quality ${item.qualityScore}/100` : ''}
                  </p>
                  {reel?.titleTranslation && <p className="text-xs italic text-neutral-600">{reel.titleTranslation}</p>}
                </div>
                <Badge status={STATE_BADGE[item.state] ?? 'BUSY'} label={item.state.replaceAll('_', ' ').toLowerCase()} />
              </div>

              {/* Timeline (spec §12.7) */}
              <div className="mt-3 flex items-center gap-2 overflow-x-auto pb-1">
                <TimelineBox label="Reference" onShow={() => void loadMedia(item.id, 'reference')}>
                  {media[`${item.id}:reference`] ? (
                    <img src={media[`${item.id}:reference`]} alt="" className="h-full w-full object-cover" />
                  ) : (
                    <span className="text-[9px] text-neutral-600">click to load</span>
                  )}
                </TimelineBox>
                {(reel?.segments ?? []).map((s) => (
                  <TimelineBox key={s.id} label={`Part ${s.partNumber}`}>
                    <div className="flex h-full flex-col items-center justify-center gap-1">
                      <Badge status={SEG_BADGE[s.status] ?? 'BUSY'} label={s.status.toLowerCase()} />
                      {s.durationSec !== null && <span className="text-[9px] text-neutral-500">{s.durationSec.toFixed(1)}s</span>}
                    </div>
                  </TimelineBox>
                ))}
                <TimelineBox label="Joined" onShow={() => void loadMedia(item.id, 'video-combined')}>
                  {media[`${item.id}:video-combined`] ? (
                    <video src={media[`${item.id}:video-combined`]} controls className="h-full w-full object-cover" />
                  ) : (
                    <span className="text-[9px] text-neutral-600">{reel?.combinedAssetId ? 'click to load' : 'pending'}</span>
                  )}
                </TimelineBox>
                <TimelineBox label="Thumbnail" onShow={() => void loadMedia(item.id, 'thumbnail')}>
                  {media[`${item.id}:thumbnail`] ? (
                    <img src={media[`${item.id}:thumbnail`]} alt="" className="h-full w-full object-cover" />
                  ) : (
                    <span className="text-[9px] text-neutral-600">{reel?.thumbnailAssetId ? 'click to load' : 'pending'}</span>
                  )}
                </TimelineBox>
              </div>

              {reel?.caption && (
                <p className="mt-2 line-clamp-2 text-xs text-neutral-400">
                  {reel.caption} <span className="text-brand-300">{reel.hashtags.join(' ')}</span>
                </p>
              )}
              {item.schedules[0] && (
                <p className="mt-1 text-xs text-neutral-500">
                  {item.schedules[0].status === 'DONE' ? 'Published' : 'Scheduled'} {new Date(item.schedules[0].publishAt).toLocaleString()}
                </p>
              )}
              {item.publications[0] && <p className="mt-1 text-xs text-emerald-400">Facebook video ID: {item.publications[0].fbObjectId}</p>}

              <div className="mt-3 flex flex-wrap gap-2">
                {item.state === 'REVIEW_REQUIRED' && (
                  <>
                    <Button onClick={async () => { await window.pagepilot.content.approve(item.id); void load(); }}>Approve & schedule</Button>
                    <Button variant="danger" onClick={async () => { await window.pagepilot.content.reject(item.id, 'Rejected in review'); void load(); }}>Reject</Button>
                  </>
                )}
                <Button variant="ghost" onClick={() => setExpanded(expanded === item.id ? null : item.id)}>
                  {expanded === item.id ? 'Hide details' : 'Details'}
                </Button>
              </div>

              {expanded === item.id && (
                <div className="mt-3 space-y-3 rounded-lg border border-neutral-800 bg-neutral-950 p-3 text-xs">
                  {reel && (
                    <div className="grid grid-cols-2 gap-3">
                      <Detail label="Hook #" value={reel.hookNumber !== null ? String(reel.hookNumber) : '—'} />
                      <Detail label="Pain trigger" value={reel.painTrigger ?? '—'} />
                      <Detail label="Comment-bait angle" value={reel.commentBaitAngle ?? '—'} />
                      <Detail label="Scenario" value={winner ? `${winner.name}${winner.location ? ` @ ${winner.location}` : ''}` : '—'} />
                    </div>
                  )}
                  {winner?.coldOpen && <Detail label="Cold open" value={winner.coldOpen} />}
                  {winner?.finalHook && <Detail label="Final hook" value={winner.finalHook} />}
                  {item.qualityScores[0] && (
                    <div>
                      <p className="mb-1 font-semibold text-neutral-400">Review ({item.qualityScores[0].overall}/100)</p>
                      <p className="text-neutral-300">{item.qualityScores[0].explanation}</p>
                      {item.qualityScores[0].blockingIssues.length > 0 && (
                        <p className="mt-1 text-red-400">Blocking: {item.qualityScores[0].blockingIssues.join('; ')}</p>
                      )}
                    </div>
                  )}
                  {reel && reel.scenarios.length > 1 && (
                    <div>
                      <p className="mb-1 font-semibold text-neutral-400">Alternative scenarios</p>
                      <ul className="space-y-1">
                        {reel.scenarios.filter((s) => !s.isWinner).map((s) => (
                          <li key={s.id} className="text-neutral-400">{s.name} — {s.conflict ?? ''}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
}

function TimelineBox(props: { label: string; children: React.ReactNode; onShow?: () => void }) {
  return (
    <button
      onClick={props.onShow}
      className="flex shrink-0 flex-col items-center gap-1 focus:outline-none"
      style={{ cursor: props.onShow ? 'pointer' : 'default' }}
    >
      <div className="flex h-28 w-16 items-center justify-center overflow-hidden rounded-lg border border-neutral-800 bg-neutral-900">
        {props.children}
      </div>
      <span className="text-[10px] text-neutral-500">{props.label}</span>
    </button>
  );
}

function Detail(props: { label: string; value: string }) {
  return (
    <div>
      <p className="font-semibold text-neutral-500">{props.label}</p>
      <p className="text-neutral-300">{props.value}</p>
    </div>
  );
}
